<template>
    <div>
        <b-modal
            v-model="isOpen"
            size="md"
            modal-class="vue-need-bootstrap"
            lazy
            centered
            hide-footer
            @close="closeModal"
            @cancel="closeModal"
            @ok="closeModal"
            @hide="closeModal"
            :title="getTitle()"
        >
            <template v-if="!handlingTemplateVersions">
                <div class="py-1 overflow-auto" style="max-height: 340px">
                    <div class="mb-3">
                        <div class="hist-elem">
                            <p class="template-name">{{ displayName }}</p>
                            <c-button
                                class="restore-btn"
                                v-if="entityType === 'T'"
                                :icon="'fa-solid fa-trash-restore'"
                                :prioritize-content="true"
                                @click="handleTemplateVersions()"
                            ></c-button>
                        </div>
                        <div class="hist-id">{{ entityId }}</div>
                    </div>
                    <div v-if="auditEntries.length > 0" id="timeline">
                        <div
                            v-for="(entry, index) in auditEntries"
                            :key="entry.entityId + entry.date"
                            class="pb-3"
                            :class="index == auditEntries.length - 1 ? 'timeline-item-last' : 'timeline-item-common'"
                        >
                            <div class="mb-1 op-text">{{ parseOperation(entry.operation) }}</div>
                            <div class="mb-1 user-text">
                                {{ `${entry.user?.Name ?? ''} (${entry.user?.Username})` }}
                            </div>
                            <div class="mb date-text">{{ getFormattedDate(entry.date) }}</div>
                        </div>
                    </div>
                </div>
                <div class="modal-footer row pb-0">
                    <b-button
                        class="ml-auto btn back-btn btn-secondary"
                        size="sm"
                        variant="secondary"
                        @click="closeModal"
                    >
                        Close
                    </b-button>
                </div>
            </template>
            <template v-else-if="props.entityType === 'T' && handlingTemplateVersions">
                <restore-template-version
                    :template-id="entityId"
                    :template-name="displayName"
                    @go-back="handleHistory"
                    @template-version-restored="templateVersionRestored"
                ></restore-template-version>
            </template>
        </b-modal>
    </div>
</template>
<script lang="ts" setup>
import { computed, ref, watch } from 'vue';
import { AuditEntity, AuditLogWithUser, AuditOperation } from '@/typings/auditLog/auditLog';
import store from '@/store';
import { getAuditEntriesWithUsers } from '@/api/auditLog';
import { DateFormat, formatDate } from '@/utils/dates';
import CButton from '@/components/shared/CButton';
import RestoreTemplateVersion from '@/components/TemplateVersion/RestoreTemplateVersion.vue';
import { ProductVersionRestoredInfo } from '@/api/typings/product';

const props = defineProps<{
    value: boolean;
    entityType: AuditEntity;
    entityId?: string | number;
    entityCreatedDate?: Date | string;
    displayName?: string;
}>();

const loading = ref(true);
const handlingTemplateVersions = ref(false);
const auditEntries = ref<AuditLogWithUser[]>([]);
const user = computed(() => store.state.user);

const emit = defineEmits<{
    (event: 'input', value: boolean): void;
    (event: 'close'): void;
    (event: 'template-version-restored', template: ProductVersionRestoredInfo): void;
}>();

const closeModal = () => {
    handlingTemplateVersions.value = false;
    emit('close');
};

const isOpen = computed({
    get(): boolean {
        return props.value;
    },
    set(value: boolean) {
        emit('input', value);
    }
});

const templateVersionRestored = (template: ProductVersionRestoredInfo) => {
    emit('template-version-restored', template);
};

watch(
    () => props.entityId,
    () => {
        if (props.entityId && props.value) {
            getAuditEntriesWithUsers(props.entityType, `${props.entityId}`)
                .then(async (data) => {
                    if (!data.find((e) => e.operation == 'C') && props) {
                        data.unshift({
                            entity: props.entityType,
                            entityId: `${props.entityId}`,
                            operation: AuditOperation.CREATE,
                            performedByUserId: user.value!.Id,
                            date: new Date(props.entityCreatedDate!),
                            impersonatedUserId: null,
                            action: 'null',
                            details: null,
                            user: {
                                Name: store.state.user?.Name ?? '',
                                Username: store.state.user?.Username ?? '',
                                Email: '',
                                Canceled: false,
                                Disabled: false,
                                Validated: false,
                                Stores: []
                            }
                        });
                    }
                    auditEntries.value = data.reverse();
                    loading.value = false;
                })
                .catch((error) => {
                    loading.value = false;
                });
        }
    },
    { immediate: true }
);

const parseOperation = (operation: AuditOperation): string => {
    switch (operation) {
        case AuditOperation.CREATE:
            return 'Created';
        case AuditOperation.RESTORE:
            return 'Restored';
        case AuditOperation.UPDATE:
            return 'Updated';
        case AuditOperation.DELETE:
            return 'Deleted';
        default:
            return 'Unknown';
    }
};

const parseEntity = (entity: AuditEntity): string => {
    switch (entity) {
        case AuditEntity.LIBRARY:
            return 'Library';
        case AuditEntity.OPTION_SET:
            return 'Option Set';
        case AuditEntity.PRODUCT:
            return 'Product';
        case AuditEntity.TEMPLATE:
            return 'Template';
        default:
            return 'Unknown';
    }
};

const getTitle = (): string => {
    return handlingTemplateVersions.value ? 'Restore template version' : `${parseEntity(props.entityType)} history`;
};

const getFormattedDate = (date: Date): string => {
    return formatDate(date, DateFormat.MMMddYYYYTime);
};

const handleHistory = () => {
    handlingTemplateVersions.value = false;
};

const handleTemplateVersions = () => {
    handlingTemplateVersions.value = true;
};
</script>

<style lang="scss" scoped>
#timeline .timeline-item-common {
    border-left: 1px solid #bfbfbf;
    padding-inline-start: 20px;
    position: relative;
    margin-left: 4px;
}

#timeline .timeline-item-last {
    padding-inline-start: 20px;
    position: relative;
    margin-left: 4px;
}

#timeline .timeline-item-common:after {
    position: absolute;
    display: block;
    top: 0;
    border-radius: 145px;
    border: 1px solid var(--Gray-scale-gray, #bfbfbf);
    background: var(--Gray-scale-white, #fefefe);
    left: -5px;
    height: 10px;
    width: 10px;
    content: '';
}

#timeline .timeline-item-last:after {
    position: absolute;
    display: block;
    top: 0;
    border-radius: 145px;
    border: 1px solid var(--Gray-scale-gray, #bfbfbf);
    background: var(--Gray-scale-white, #fefefe);
    left: -4px;
    height: 10px;
    width: 10px;
    content: '';
}

.hist-elem {
    display: flex;
    justify-content: flex-end;
    color: var(--Gray-scale-gray-darkest, #333);
    font-size: 16px;
    font-style: normal;
    font-weight: 700;
    line-height: normal;

    .template-name {
        width: 100%;
        margin-bottom: 5px;
    }
}

.hist-id {
    color: var(--Primary-primary, #7258e0);
    font-size: 12px;
    font-style: normal;
    font-weight: 400;
    line-height: normal;
}

.op-text {
    color: #8a8a8a;
    font-size: 12px;
    font-style: normal;
    font-weight: 400;
    line-height: normal;
}

.user-text {
    color: var(--Gray-scale-gray-darkest, #333);
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: normal;
}

.date-text {
    color: #565656;
    font-size: 12px;
    font-style: normal;
    font-weight: 400;
    line-height: normal;
}

.restore-btn {
    padding: 5px !important;
    line-height: 1 !important;
}
</style>
